/* Admin Users & Access — create users, custom roles with policies, deactivate. */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserCog, Plus, ShieldCheck, Trash2, X } from "lucide-react";

export default function AdminAccess() {
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [staff, setStaff] = useState([]);
  const [showUser, setShowUser] = useState(false);
  const [showRole, setShowRole] = useState(null); // null | "new" | role obj

  const load = async () => {
    const [r, p, u] = await Promise.all([
      api.get("/admin/roles"), api.get("/admin/permissions"),
      api.get("/admin/users", { params: { role: "staff" } }),
    ]);
    setRoles(r.data.roles || []); setPerms(p.data.permissions || []); setStaff(u.data.users || []);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "User deactivated" : "User reactivated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delRole = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try { await api.delete(`/admin/roles/${r.id}`); toast.success("Role deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="admin-access-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ users &amp; access</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter inline-flex items-center gap-2"><UserCog size={26} /> Users &amp; Access</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowRole("new")} data-testid="new-role-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold rounded-md border border-black/15 hover:border-black">
            <ShieldCheck size={14} /> New role
          </button>
          <button onClick={() => setShowUser(true)} data-testid="new-user-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold rounded-md bg-[#0A0C10] text-white hover:bg-black">
            <Plus size={14} /> Create user
          </button>
        </div>
      </div>

      {/* Roles */}
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3">/ roles &amp; policies</div>
        {roles.length === 0 ? (
          <div className="bg-white border border-dashed border-black/15 rounded-xl p-8 text-center text-sm text-neutral-500">
            No custom roles yet — create one (e.g. "Call Quality") and pick its policies.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {roles.map((r) => (
              <div key={r.id} className="bg-white border border-neutral-200 rounded-xl p-4" data-testid={`role-card-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-bold">{r.name}</div>
                  <div className="flex gap-1">
                    <button onClick={() => setShowRole(r)} data-testid={`role-edit-${r.id}`} className="text-xs px-2 py-1 rounded border border-neutral-300 hover:border-black">Edit</button>
                    <button onClick={() => delRole(r)} data-testid={`role-delete-${r.id}`} className="text-xs px-2 py-1 rounded border border-red-200 text-red-500 hover:bg-red-50"><Trash2 size={11} /></button>
                  </div>
                </div>
                <div className="text-[11px] text-neutral-500 mt-1">{r.user_count || 0} user(s)</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(r.permissions || []).map((p) => (
                    <span key={p} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-neutral-100 border border-neutral-200">{p}</span>
                  ))}
                  {(r.permissions || []).length === 0 && <span className="text-[11px] text-neutral-400">no policies</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Staff users */}
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3">/ staff users</div>
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">
              <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Phone</th><th className="px-5 py-3">Role</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Action</th></tr>
            </thead>
            <tbody>
              {staff.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-neutral-500">No staff users yet — create one and hand them the login.</td></tr>
              ) : staff.map((u) => (
                <tr key={u.id} className="border-t border-neutral-100" data-testid={`staff-row-${u.id}`}>
                  <td className="px-5 py-3 font-semibold">{u.full_name}</td>
                  <td className="px-5 py-3 text-neutral-600">{u.email}</td>
                  <td className="px-5 py-3 font-mono text-xs">{u.phone || "—"}</td>
                  <td className="px-5 py-3 text-xs">{roles.find((r) => r.id === u.role_id)?.name || <span className="text-neutral-400">no role</span>}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${u.is_active !== false ? "text-emerald-700 border-emerald-300 bg-emerald-50" : "text-red-600 border-red-200 bg-red-50"}`}>
                      {u.is_active !== false ? "active" : "disabled"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => toggleActive(u)} data-testid={`staff-toggle-${u.id}`}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-md border ${u.is_active !== false ? "border-red-200 text-red-600 hover:bg-red-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
                      {u.is_active !== false ? "Deactivate" : "Reactivate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showUser && <CreateUserModal roles={roles} onClose={() => setShowUser(false)} onSaved={() => { setShowUser(false); load(); }} />}
      {showRole && <RoleModal role={showRole === "new" ? null : showRole} perms={perms} onClose={() => setShowRole(null)} onSaved={() => { setShowRole(null); load(); }} />}
    </div>
  );
}

function CreateUserModal({ roles, onClose, onSaved }) {
  const [f, setF] = useState({ full_name: "", email: "", phone: "", password: "", role: "staff", role_id: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/users/create", f);
      toast.success(`User created — they'll set a new password on first login`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  const inp = "w-full border border-neutral-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0A0C10]";
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="create-user-modal">
      <div className="bg-white w-full max-w-md rounded-2xl p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h2 className="font-display font-bold text-lg">Create user</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-neutral-100 rounded-md"><X size={15} /></button></div>
        <input className={inp} placeholder="Full name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="cu-name" />
        <input className={inp} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="cu-email" />
        <input className={inp} placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="cu-phone" />
        <input className={inp} placeholder="Temporary password (min 6 chars)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="cu-password" />
        <div className="flex gap-2">
          <select className={inp} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="cu-role">
            <option value="staff">Staff (call quality etc.)</option>
            <option value="teacher">Teacher</option>
            <option value="admin">Admin</option>
          </select>
          {f.role === "staff" && (
            <select className={inp} value={f.role_id} onChange={(e) => setF({ ...f, role_id: e.target.value })} data-testid="cu-role-id">
              <option value="">Pick access role…</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}
        </div>
        <p className="text-[11px] text-neutral-500">The user must change this password on their first login.</p>
        <button onClick={save} disabled={saving} data-testid="cu-save"
          className="w-full py-2.5 rounded-md bg-[#0A0C10] text-white font-bold text-sm hover:bg-black disabled:opacity-60">
          {saving ? "Creating…" : "Create user"}
        </button>
      </div>
    </div>
  );
}

function RoleModal({ role, perms, onClose, onSaved }) {
  const [name, setName] = useState(role?.name || "");
  const [sel, setSel] = useState(role?.permissions || []);
  const [saving, setSaving] = useState(false);
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const save = async () => {
    setSaving(true);
    try {
      if (role) await api.patch(`/admin/roles/${role.id}`, { name, permissions: sel });
      else await api.post("/admin/roles", { name, permissions: sel });
      toast.success(role ? "Role updated" : "Role created");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="role-modal">
      <div className="bg-white w-full max-w-md rounded-2xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h2 className="font-display font-bold text-lg">{role ? "Edit role" : "New role"}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-neutral-100 rounded-md"><X size={15} /></button></div>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder='Role name (e.g. "Call Quality")' data-testid="role-name-input"
          className="w-full border border-neutral-300 rounded-md px-3 py-2.5 text-sm font-semibold outline-none focus:border-[#0A0C10]" />
        <div className="space-y-2">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">Policies</div>
          {perms.map((p) => (
            <label key={p.key} className="flex items-start gap-2.5 text-sm cursor-pointer" data-testid={`perm-${p.key}`}>
              <input type="checkbox" checked={sel.includes(p.key)} onChange={() => toggle(p.key)} className="mt-0.5" />
              <span><strong className="font-semibold">{p.key}</strong><br /><span className="text-xs text-neutral-500">{p.label}</span></span>
            </label>
          ))}
        </div>
        <button onClick={save} disabled={saving || !name.trim()} data-testid="role-save"
          className="w-full py-2.5 rounded-md bg-[#0A0C10] text-white font-bold text-sm hover:bg-black disabled:opacity-50">
          {saving ? "Saving…" : "Save role"}
        </button>
      </div>
    </div>
  );
}
